import {createStyles} from '@mantine/core';
import React from 'react';

import {openSampleEntry, openSampleSequence} from 'actions/OnboardingActions';
import * as analytics from 'analytics/Analytics';
import * as NavConstants from 'constants/NavConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus} from 'llm/chat-library/ChatActions';
import {openDraftChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as NewUserFlowActions from 'onboarding/new-user-flow/NewUserFlowActions';
import InfoSidePanel from 'onboarding/new-user-flow/signup-flow-v2/InfoSidePanel';
import SignupScreen from 'onboarding/new-user-flow/signup-flow-v2/SignupScreen';
import SignupScreenCard from 'onboarding/new-user-flow/signup-flow-v2/SignupScreenCard';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';

interface ApplicationCardPanelProps {
  hoverOff: () => void;
  hoverNotebook: () => void;
  hoverMolBio: () => void;
}

const useStyles = createStyles((theme, {compact}: {compact: boolean}) => ({
  row: {
    marginLeft: '52px',
    marginRight: '52px',
    height: '100%',
  },
  cardColumn: {
    height: '100%',
  },
  buttonPanel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: compact ? 'center' : 'space-evenly',
    height: '100%',
    gap: compact ? theme.spacing.md : undefined,
    padding: compact ? `${theme.spacing.lg} 0` : `${theme.spacing.xl} 0`,
  },
}));

function ApplicationCardPanel(props: ApplicationCardPanelProps): JSX.Element {
  const notebookBodyText = 'Plan multi-day experiments, centralize data, and share experimental results.';
  const molBioBodyText =
    'Annotate sequences, perform alignments, and design plasmids, primers, and CRISPR guides.';
  const benchlingAIBodyText =
    'Ask Benchling AI to capture data, analyze results, and draft reports across your work.';
  const handleClickNotebook = (): void => {
    analytics.track('onboarding_page', 'clicked', {
      page: 'molbio_or_notebook',
      objectClicked: 'button',
      objectText: 'Notebook',
      eventLastModified: 'FY22_Q2b3',
    });
    NewUserFlowActions.hideScreen();
    openSampleEntry();
  };
  const handleClickMolBio = (): void => {
    analytics.track('onboarding_page', 'clicked', {
      page: 'molbio_or_notebook',
      objectClicked: 'button',
      objectText: 'Molecular Biology',
      eventLastModified: 'FY22_Q2b3',
    });
    NewUserFlowActions.hideScreen();
    openSampleSequence();
  };
  const showBenchlingAI = isAgentTypeEnabled(AgentType.ASSISTANT);
  const {classes} = useStyles({compact: showBenchlingAI});
  const handleClickBenchlingAI = (): void => {
    analytics.track('onboarding_page', 'clicked', {
      page: 'molbio_or_notebook',
      objectClicked: 'button',
      objectText: 'Benchling AI',
      eventLastModified: 'FY22_Q2b3',
    });
    NewUserFlowActions.hideScreen();
    // Open a draft chat rather than eagerly creating a chat session, matching the homepage AI
    // entry points, so the assistant opens ready for input without a spurious "Running now" task.
    openDraftChat({
      status: ChatDraftStatus.DRAFT,
      message: {text: '', selectedFiles: []},
      settings: {agentType: AgentType.ASSISTANT, publicDataEnabled: false},
    });
    SidepanelActions.openSection(NavConstants.SECTIONS.AI);
  };
  return (
    <div className={classes.buttonPanel}>
      <SignupScreenCard
        titleText="Notebook"
        bodyText={notebookBodyText}
        imagePath="/static/img/new-user-flow/notebook.png"
        onClick={handleClickNotebook}
        buttonText="Show me an entry"
        hoverOn={props.hoverNotebook}
        hoverOff={props.hoverOff}
        compact={showBenchlingAI}
      />
      <SignupScreenCard
        titleText="Molecular Biology"
        bodyText={molBioBodyText}
        imagePath="/static/img/new-user-flow/molbio.png"
        onClick={handleClickMolBio}
        buttonText="Open a sequence"
        hoverOn={props.hoverMolBio}
        hoverOff={props.hoverOff}
        compact={showBenchlingAI}
      />
      {showBenchlingAI && (
        <SignupScreenCard
          titleText="Benchling AI"
          bodyText={benchlingAIBodyText}
          imagePath="/static/img/new-user-flow/ai.png"
          onClick={handleClickBenchlingAI}
          buttonText="Open Benchling AI"
          hoverOff={props.hoverOff}
          compact={true}
        />
      )}
    </div>
  );
}

interface MolBioOrNotebookScreenProps {
  hoverOff: () => void;
  hoverNotebook: () => void;
  hoverMolBio: () => void;
}

export default function MolBioOrNotebookScreen(props: MolBioOrNotebookScreenProps): JSX.Element {
  const titleText = 'Choose what you want to see first';
  const bodyText =
    'Benchling offers a suite of capabilities for capturing, analyzing, and sharing your data.';
  const showBenchlingAI = isAgentTypeEnabled(AgentType.ASSISTANT);
  const {classes} = useStyles({compact: showBenchlingAI});
  return (
    <SignupScreen tall={showBenchlingAI}>
      <Row className={classes.row}>
        <Col xs={4} sm={6}>
          <InfoSidePanel titleText={titleText} bodyText={bodyText} />
        </Col>
        <Col xs={8} sm={6} className={classes.cardColumn}>
          <ApplicationCardPanel {...props} />
        </Col>
      </Row>
    </SignupScreen>
  );
}
